import ErrorBoundary from "src/components/ErrorBoundary";
import React, { Suspense, lazy, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import AuthService, { InfoServerResponse } from "src/config/AuthService";
import useLoadEffect from "src/config/hooks/useLoadEffect";
import InitializationError from "src/components/InitializationError";
import LoadingPage from "src/views/LoadingPage";
import { AxiosResponse } from "axios";

const UserLayout = lazy(() => import("src/layouts/User/UserLayout"));
const StoreProvider = lazy(() => import("src/context/StoreProvider"));
const LoginLayout = lazy(() => import("src/layouts/Login/LoginLayout"));
const MaintenanceLayout = lazy(
  () => import("src/layouts/Maintenance/MaintenanceLayout"),
);

export interface IEnv {
  API_URI: string;
  BUILD_VERSION: string;
  ENVIRONMENT: string;
  MESSAGE_MAINTENANCE_TITLE: string;
  MESSAGE_MAINTENANCE: string[];
  SHOW_MESSAGE_MAINTENANCE: boolean;
  UPLOAD_MAX_SIZE_MO: number;
  POURCENTAGE_SURFACE_CONTRAT: number;
  POURCENTAGE_SURFACE_PARCELLES_CONTRAT_INFERIEUR: number;
}

declare global {
  interface Window {
    env: IEnv;
  }
}

const loadWindowEnv = async (signal?: AbortSignal) => {
  const res: AxiosResponse<InfoServerResponse> =
    await AuthService.getInfosServer();
  if (signal?.aborted) return;
  window.env.ENVIRONMENT = res.data.environment;
  window.env.MESSAGE_MAINTENANCE = res.data.messagemaintenance;
  window.env.MESSAGE_MAINTENANCE_TITLE = res.data.messagemaintenancetitle;
  window.env.SHOW_MESSAGE_MAINTENANCE = res.data.showmessagemaintenance;
  window.env.UPLOAD_MAX_SIZE_MO = res.data.uploadmaxsizemo;
  window.env.POURCENTAGE_SURFACE_CONTRAT = res.data.pourcentagesurfacecontrat;
  window.env.POURCENTAGE_SURFACE_PARCELLES_CONTRAT_INFERIEUR =
    res.data.pourcentagesurfaceparcellescontratinferieur ?? 5;
};

export default function App(): React.JSX.Element {
  const [sessionReady, setSessionReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [initializationError, setInitializationError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useLoadEffect(
    async (signal) => {
      setInitializationError(false);
      setSessionReady(false);
      try {
        const [, loggedIn] = await Promise.all([
          loadWindowEnv(signal),
          AuthService.checkSession(),
        ]);
        if (!signal.aborted) {
          setIsLoggedIn(loggedIn);
          setSessionReady(true);
        }
      } catch (error) {
        if (!signal.aborted) {
          console.error(
            "Impossible d'initialiser la session utilisateur",
            error,
          );
          setInitializationError(true);
        }
      }
    },
    [attempt],
  );

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingPage />}>
        <div
          className="App"
          style={{
            background:
              "linear-gradient(45deg, rgba(35,47,52,1) 0%, rgba(48,63,69,1) 22%, rgba(35,47,52,1) 100%)",
            backgroundColor: "#232F34",
            color: "#FFFFFF !important",
            backgroundSize: "cover",
            backgroundAttachment: "fixed",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            overflowX: "auto",
          }}
        >
          <BrowserRouter>
            {initializationError ? (
              <InitializationError
                retry={() => setAttempt((value) => value + 1)}
              />
            ) : (
              !sessionReady && <LoadingPage />
            )}
            {sessionReady &&
              !isLoggedIn &&
              !(
                window.env.SHOW_MESSAGE_MAINTENANCE &&
                !!window.env.MESSAGE_MAINTENANCE &&
                window.env.MESSAGE_MAINTENANCE.length > 0
              ) && <LoginLayout />}
            {sessionReady &&
              isLoggedIn &&
              !(
                window.env.SHOW_MESSAGE_MAINTENANCE &&
                !!window.env.MESSAGE_MAINTENANCE &&
                window.env.MESSAGE_MAINTENANCE.length > 0
              ) && (
                <StoreProvider>
                  <UserLayout />
                </StoreProvider>
              )}
            {sessionReady &&
              window.env.SHOW_MESSAGE_MAINTENANCE &&
              !!window.env.MESSAGE_MAINTENANCE &&
              window.env.MESSAGE_MAINTENANCE.length > 0 && (
                <MaintenanceLayout />
              )}
          </BrowserRouter>
        </div>
      </Suspense>
    </ErrorBoundary>
  );
}
